/**
 * VanillaFeedbackWidgetV2 — Cross-venture feedback widget without React dependency.
 *
 * Drop-in script for Patchwise managed client sites and any page without React.
 * Supports anonymous feedback with optional email and tracking links.
 *
 * Usage:
 *   <script src="https://cdn.uitl.ai/sdk/v2/uitl-feedback.min.js"></script>
 *   <script>
 *     new UITLFeedback({
 *       venture: 'Patchwise',
 *       apiEndpoint: 'https://api.patchwise.ai',
 *       primaryColor: '#4F46E5',
 *     });
 *   </script>
 */

export interface FeedbackWidgetConfig {
  venture: string;
  apiEndpoint: string;
  primaryColor?: string;
  position?: 'bottom-right' | 'bottom-left';
  bottomOffset?: number;
  showEmailCapture?: boolean;
  user?: { id?: string; email?: string; is_admin?: boolean } | null;
  feedbackTypes?: ('bug' | 'feature' | 'feedback')[];
}

export class VanillaFeedbackWidgetV2 {
  private config: Required<FeedbackWidgetConfig>;
  private container: HTMLDivElement;
  private isOpen = false;
  private isSubmitting = false;
  private isSubmitted = false;
  private trackingUrl: string | null = null;
  private type: string;
  private title = '';
  private description = '';
  private email = '';
  private autoFix = false;
  private error: string | null = null;
  private navHistory: string[] = [];

  constructor(config: FeedbackWidgetConfig) {
    this.config = {
      primaryColor: '#4F46E5',
      position: 'bottom-right',
      bottomOffset: 24,
      showEmailCapture: true,
      user: null,
      feedbackTypes: ['bug', 'feature'],
      ...config,
    };
    this.type = this.config.feedbackTypes[0] || 'bug';
    this.container = document.createElement('div');
    this.container.id = 'uitl-feedback-widget-v2';
    document.body.appendChild(this.container);

    // Track navigation
    this.navHistory = [window.location.pathname];
    window.addEventListener('popstate', () => {
      this.navHistory = [
        window.location.pathname,
        ...this.navHistory.filter(p => p !== window.location.pathname),
      ].slice(0, 5);
    });

    this.render();
  }

  private render(): void {
    const { primaryColor, position, bottomOffset } = this.config;
    const posStyle = position === 'bottom-right' ? 'right: 24px' : 'left: 24px';

    if (!this.isOpen) {
      this.container.innerHTML = `
        <button id="uitl-fb-fab" style="
          position: fixed; ${posStyle}; bottom: ${bottomOffset}px;
          width: 56px; height: 56px; border-radius: 50%;
          background: ${primaryColor}; color: #fff; border: none;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3); z-index: 99999;
          transition: transform 0.2s;
        " aria-label="Submit feedback">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>`;
      this.container.querySelector('#uitl-fb-fab')?.addEventListener('click', () => {
        this.isOpen = true;
        this.isSubmitted = false;
        this.trackingUrl = null;
        this.title = '';
        this.description = '';
        this.autoFix = false;
        this.error = null;
        this.render();
      });
      return;
    }

    if (this.isSubmitted) {
      this.container.innerHTML = `
        <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:100000;display:flex;align-items:center;justify-content:center;">
          <div style="background:#1e293b;border-radius:12px;padding:24px;width:90%;max-width:480px;color:#e2e8f0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;text-align:center;">
            <div style="color:#22c55e;font-size:40px;margin-bottom:8px;">✓</div>
            <h3 style="color:#22c55e;margin:0 0 8px;font-size:18px;font-weight:600;">Thank you!</h3>
            <p style="color:#94a3b8;font-size:14px;margin:0 0 12px;">
              ${this.autoFix ? 'Auto-fix queued for Patch McFixitface' : 'Your feedback has been received.'}
            </p>
            ${this.trackingUrl ? `
              <div style="background:#0f172a;border:1px solid ${primaryColor};border-radius:8px;padding:12px 16px;margin-top:12px;">
                <p style="font-size:12px;color:#94a3b8;margin:0 0 8px;">Track your feedback:</p>
                <a href="${this.trackingUrl}" target="_blank" rel="noopener" style="color:${primaryColor};font-weight:600;text-decoration:none;">View Status</a>
              </div>
            ` : ''}
            <button id="uitl-fb-close" style="margin-top:16px;padding:10px 24px;background:transparent;color:#94a3b8;border:1px solid #334155;border-radius:8px;cursor:pointer;font-size:14px;">Close</button>
          </div>
        </div>`;
      this.container.querySelector('#uitl-fb-close')?.addEventListener('click', () => { this.isOpen = false; this.render(); });
      return;
    }

    const types = this.config.feedbackTypes;
    const typeButtons = types.map(t => {
      const active = this.type === t;
      const label = t === 'bug' ? '🐛 Bug' : t === 'feature' ? '💡 Feature' : '💬 Feedback';
      return `<button class="uitl-fb-type" data-type="${t}" style="
        padding:8px 16px;border:1px solid ${active ? primaryColor : '#334155'};
        border-radius:8px;background:${active ? primaryColor + '20' : 'transparent'};
        color:${active ? primaryColor : '#94a3b8'};cursor:pointer;font-size:13px;
        font-weight:${active ? 600 : 400};
      ">${label}</button>`;
    }).join('');

    const showEmail = this.config.showEmailCapture && !this.config.user;
    const showAutoFixToggle = this.config.user?.is_admin;

    this.container.innerHTML = `
      <div id="uitl-fb-overlay" style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:100000;display:flex;align-items:center;justify-content:center;">
        <div style="background:#1e293b;border-radius:12px;padding:24px;width:90%;max-width:480px;max-height:90vh;overflow:auto;color:#e2e8f0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
          <h3 style="margin:0 0 16px;font-size:18px;font-weight:600;color:#f1f5f9;">Send Feedback</h3>
          <div style="display:flex;gap:8px;margin-bottom:16px;">${typeButtons}</div>

          <label style="display:block;font-size:12px;color:#94a3b8;margin-bottom:4px;font-weight:500;">Title *</label>
          <input id="uitl-fb-title" type="text" maxlength="255" placeholder="${this.type === 'bug' ? 'What went wrong?' : 'What would you like?'}" value="${this._esc(this.title)}" style="width:100%;padding:10px 12px;background:#0f172a;border:1px solid #334155;border-radius:8px;color:#e2e8f0;font-size:14px;outline:none;margin-bottom:12px;box-sizing:border-box;"/>

          <label style="display:block;font-size:12px;color:#94a3b8;margin-bottom:4px;font-weight:500;">${this.type === 'bug' ? 'Steps to reproduce (optional)' : 'Details (optional)'}</label>
          <textarea id="uitl-fb-desc" rows="3" placeholder="${this.type === 'bug' ? 'What happened? What did you expect?' : 'Any additional context...'}" style="width:100%;padding:10px 12px;background:#0f172a;border:1px solid #334155;border-radius:8px;color:#e2e8f0;font-size:14px;outline:none;margin-bottom:12px;resize:vertical;min-height:80px;box-sizing:border-box;font-family:inherit;">${this._esc(this.description)}</textarea>

          ${showEmail ? `
            <label style="display:block;font-size:12px;color:#94a3b8;margin-bottom:4px;font-weight:500;">Email (optional — for status updates)</label>
            <input id="uitl-fb-email" type="email" placeholder="your@email.com" value="${this._esc(this.email)}" style="width:100%;padding:10px 12px;background:#0f172a;border:1px solid #334155;border-radius:8px;color:#e2e8f0;font-size:14px;outline:none;margin-bottom:12px;box-sizing:border-box;"/>
          ` : ''}

          ${showAutoFixToggle ? `
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 0;">
              <input type="checkbox" id="uitl-fb-autofix" ${this.autoFix ? 'checked' : ''} style="accent-color:${primaryColor}"/>
              <label for="uitl-fb-autofix" style="font-size:13px;color:${this.autoFix ? primaryColor : '#94a3b8'};font-weight:${this.autoFix ? 600 : 400};cursor:pointer;">⚡ Route to Patch McFixitface</label>
            </div>
          ` : ''}

          ${this.error ? `<p style="color:#ef4444;font-size:13px;margin:0 0 12px;">${this._esc(this.error)}</p>` : ''}

          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:8px;">
            <button id="uitl-fb-cancel" style="padding:10px 24px;background:transparent;color:#94a3b8;border:1px solid #334155;border-radius:8px;cursor:pointer;font-size:14px;">Cancel</button>
            <button id="uitl-fb-submit" ${!this.title.trim() || this.isSubmitting ? 'disabled' : ''} style="padding:10px 24px;background:${!this.title.trim() || this.isSubmitting ? '#334155' : primaryColor};color:#fff;border:none;border-radius:8px;cursor:${!this.title.trim() || this.isSubmitting ? 'default' : 'pointer'};font-size:14px;font-weight:600;opacity:${!this.title.trim() || this.isSubmitting ? 0.5 : 1};">
              ${this.isSubmitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>`;

    // Event listeners
    this.container.querySelector('#uitl-fb-overlay')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'uitl-fb-overlay') { this.isOpen = false; this.render(); }
    });
    this.container.querySelector('#uitl-fb-cancel')?.addEventListener('click', () => { this.isOpen = false; this.render(); });
    this.container.querySelector('#uitl-fb-title')?.addEventListener('input', (e) => { this.title = (e.target as HTMLInputElement).value; });
    this.container.querySelector('#uitl-fb-desc')?.addEventListener('input', (e) => { this.description = (e.target as HTMLTextAreaElement).value; });
    this.container.querySelector('#uitl-fb-email')?.addEventListener('input', (e) => { this.email = (e.target as HTMLInputElement).value; });
    this.container.querySelector('#uitl-fb-autofix')?.addEventListener('change', (e) => { this.autoFix = (e.target as HTMLInputElement).checked; });
    this.container.querySelectorAll('.uitl-fb-type').forEach(btn => {
      btn.addEventListener('click', () => { this.type = btn.getAttribute('data-type') || 'bug'; this.render(); });
    });
    this.container.querySelector('#uitl-fb-submit')?.addEventListener('click', () => this.submit());
  }

  private async submit(): Promise<void> {
    if (!this.title.trim() || this.isSubmitting) return;
    this.isSubmitting = true;
    this.error = null;
    this.render();

    try {
      const resp = await fetch(`${this.config.apiEndpoint}/api/v1/feedback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: this.type,
          title: this.title.trim(),
          description: this.description.trim() || null,
          page: window.location.pathname,
          source_user_email: this.email.trim() || undefined,
          auto_fix: this.autoFix,
          user_context: {
            navigation_history: this.navHistory,
            browser_info: navigator.userAgent,
            window_size: `${window.innerWidth}x${window.innerHeight}`,
          },
        }),
      });

      if (!resp.ok) throw new Error(`Submit failed: ${resp.status}`);
      const data = await resp.json();

      this.isSubmitted = true;
      this.trackingUrl = data.tracking_url || null;
    } catch (e: any) {
      this.error = e.message || 'Failed to submit';
    } finally {
      this.isSubmitting = false;
      this.render();
    }
  }

  private _esc(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  public destroy(): void {
    this.container.remove();
  }
}

// Auto-register on window for CDN usage
if (typeof window !== 'undefined') {
  (window as any).UITLFeedback = VanillaFeedbackWidgetV2;
}
